import {
  Checkbox,
  Grid,
  Box,
  FormControlLabel,
  Stack,
  FormHelperText,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import TooltipField from 'src/components/atoms/TooltipField';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parseISO } from 'date-fns';
import AiButton from '../ai/AiButton';
import { CustomField } from './CustomField';
import React from 'react';

type ResolvedType = 'date' | 'checkbox' | 'select' | 'multiline' | 'text';

interface FormFieldProps {
  fieldProps: any;
  formikProps?: any;
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
}

export const FormField = ({
  fieldProps,
  formikProps,
  children,
  sx,
}: FormFieldProps) => {
  const label =
    fieldProps.label ??
    (fieldProps.name?.charAt(0).toUpperCase() + fieldProps.name?.slice(1));
  const onChangeFunction =
    fieldProps?.handleChange ?? formikProps?.handleChange;

  const { t }: { t: any } = useTranslation();

  const resolvedType: ResolvedType =
    fieldProps.type ??
    (fieldProps.date
      ? 'date'
      : fieldProps.checkbox
      ? 'checkbox'
      : fieldProps.select
      ? 'select'
      : fieldProps.multiLine
      ? 'multiline'
      : 'text');

  const name = fieldProps.name;
  const touched = formikProps?.touched?.[name];
  const error = formikProps?.errors?.[name];
  const hasSubmitted = (formikProps?.submitCount ?? 0) > 0;
  const showError = (touched || hasSubmitted) && error;

  // full status object
  const status = formikProps?.status ?? {};
  const showAllTooltips = Boolean(status.showAllTooltips);
  const tooltipText: string | undefined = fieldProps.tooltipText;

  // Hints from formik.status.hints
  const hints = status?.hints ?? {};
  const hintForField = hints?.[name];
  const showHint = !showError && Boolean(hintForField);

  // Field meta (warning, etc) from schema
  const fieldMeta = status?.fieldMeta?.[name] ?? {};
  const metaWarning: string | undefined = fieldMeta.warning;

  const value = formikProps?.values?.[name];
  const isEmpty =
    value === '' || value === null || typeof value === 'undefined';

  const showWarning =
    !showError &&
    !showHint &&
    Boolean(metaWarning) &&
    (touched || hasSubmitted) &&
    isEmpty;

  const baseHelperText = showError
    ? error
    : showHint
    ? hintForField
    : showWarning
    ? metaWarning
    : '';

  // If info-button mode is on and there is tooltipText, use it as info helper text
  const useTooltipInfo =
    !baseHelperText && !showError && showAllTooltips && !!tooltipText;

  const helperText = useTooltipInfo ? tooltipText : baseHelperText;

  const isError = Boolean(showError);
  const isInfo = !isError && (showHint || useTooltipInfo);
  const isWarning = !isError && !isInfo && showWarning;

  return (
    <>
      <Grid
        item
        display="flex"
        md={fieldProps.md ?? 12}
        xs={fieldProps.xs ?? 5.8}
      >
        <Grid
          item
          md={12}
          xs={12}
          display="flex"
          alignItems="center"
          width="100%"
        >
          {resolvedType === 'date' ? (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label={label}
                ampm={false}
                disabled={fieldProps.disabled ?? false}
                value={
                  formikProps?.values?.[fieldProps.name] &&
                  formikProps?.values?.[fieldProps.name] !== ''
                    ? parseISO(formikProps?.values?.[fieldProps.name])
                    : null
                }
                onChange={(value) => {
                  formikProps.setFieldValue(
                    fieldProps.name,
                    value ? new Date(value).toISOString() : null
                  );
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: 'normal',
                    name: fieldProps.name,
                    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                      formikProps.setFieldTouched(fieldProps.name, true);
                      formikProps.handleBlur(e);
                    },
                    error: Boolean(isError),
                    helperText,
                    sx,
                    FormHelperTextProps: isInfo
                      ? { sx: { color: 'info.main' } }
                      : isWarning
                      ? { sx: { color: 'warning.main' } }
                      : undefined,
                  },
                }}
              />
            </LocalizationProvider>
          ) : resolvedType === 'checkbox' ? (
            <Box display="flex" alignItems="center">
              <FormControlLabel
                sx={sx}
                control={
                  <Checkbox
                    name={fieldProps.name}
                    checked={
                      toBoolean(formikProps?.values?.[fieldProps.name]) ?? false
                    }
                    value={formikProps?.values?.[fieldProps.name] ?? false}
                    onBlur={formikProps?.handleBlur}
                    onChange={onChangeFunction}
                  />
                }
                label={label}
              />
              <Stack display="flex" spacing={2}>
                {Boolean(isError) && (
                  <FormHelperText error>{helperText}</FormHelperText>
                )}
                {!isError && isInfo && (
                  <FormHelperText sx={{ color: 'info.main' }}>
                    {helperText}
                  </FormHelperText>
                )}
                {!isError && !isInfo && isWarning && (
                  <FormHelperText sx={{ color: 'warning.main' }}>
                    {helperText}
                  </FormHelperText>
                )}
              </Stack>
            </Box>
          ) : (
            <CustomField
              options={{
                ...fieldProps,
                tooltipText: fieldProps.tooltipText,
                select: fieldProps.select ?? (resolvedType === 'select'),
                multiLine:
                  fieldProps.multiLine ?? (resolvedType === 'multiline'),
              }}
              stateProps={formikProps}
              sx={fieldProps.sx ?? sx}
            >
              {children}
            </CustomField>
          )}

          <TooltipField tooltipText={fieldProps.tooltipText} />
          {fieldProps.aiField ? (
            <AiButton
              formikProps={formikProps}
              fieldName={fieldProps.name}
              fieldInstructions={fieldProps?.aiInstructions ?? null}
            />
          ) : null}
        </Grid>
      </Grid>
    </>
  );
};

const toBoolean = (value: any) => {
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
  return Boolean(value);
};
